import React, { useState } from 'react';
import {
  Box,
  Container,
  Tabs,
  Tab,
  Typography,
  Paper,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BugReportIcon from '@mui/icons-material/BugReport';
import ReceiptsList from '../components/financial/ReceiptsList';
import PaymentVouchersList from '../components/financial/PaymentVouchersList';
import FinancialDebugger from '../components/financial/FinancialDebugger';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`financial-tabpanel-${index}`}
      aria-labelledby={`financial-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `financial-tab-${index}`,
    'aria-controls': `financial-tabpanel-${index}`,
  };
}

const FinancialPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [showDebugger, setShowDebugger] = useState(false);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleRefresh = () => {
    console.log('Financial data refreshed');
    // Any additional refresh logic can be added here
  };

  const toggleDebugger = () => {
    setShowDebugger(prev => !prev);
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ my: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4" component="h1" gutterBottom>
            إدارة المالية
          </Typography>
          <Button 
            variant="outlined" 
            color="secondary" 
            startIcon={<BugReportIcon />}
            onClick={toggleDebugger}
          >
            {showDebugger ? "إخفاء أداة التشخيص" : "عرض أداة التشخيص"}
          </Button>
        </Box>

        {showDebugger && <FinancialDebugger />}

        <Paper sx={{ width: '100%', mb: 2 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange} 
              aria-label="financial tabs"
              textColor="primary"
              indicatorColor="primary"
              centered
            >
              <Tab label="سندات القبض" {...a11yProps(0)} />
              <Tab label="سندات الصرف" {...a11yProps(1)} />
              <Tab label="التقارير المالية" {...a11yProps(2)} />
            </Tabs>
          </Box>
          
          <TabPanel value={tabValue} index={0}>
            <Accordion defaultExpanded>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="receipts-content"
                id="receipts-header"
              >
                <Typography variant="h6">قائمة سندات القبض</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <ReceiptsList onRefresh={handleRefresh} />
              </AccordionDetails>
            </Accordion>
            
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" align="center">
                ملاحظة: يجب أن تظهر سندات القبض المحفوظة تلقائياً في الجدول. إذا واجهتك مشكلة، يمكنك استخدام أداة التشخيص أعلاه.
              </Typography>
            </Box>
          </TabPanel>
          
          <TabPanel value={tabValue} index={1}>
            <Accordion defaultExpanded>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="payment-vouchers-content"
                id="payment-vouchers-header"
              >
                <Typography variant="h6">قائمة سندات الصرف</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <PaymentVouchersList onRefresh={handleRefresh} />
              </AccordionDetails>
            </Accordion>
            
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" align="center">
                ملاحظة: يجب أن تظهر سندات الصرف المحفوظة تلقائياً في الجدول. إذا واجهتك مشكلة، يمكنك استخدام أداة التشخيص أعلاه.
              </Typography>
            </Box>
          </TabPanel>
          
          <TabPanel value={tabValue} index={2}>
            {/* Financial Reports Component will go here */}
            <Typography variant="body1" align="center">
              قيد التطوير - التقارير المالية
            </Typography>
          </TabPanel>
        </Paper>
      </Box>
    </Container>
  );
};

export default FinancialPage;